import { useContext, useMemo, useRef, useState } from 'react'
import firebase from '../../../firebase/compat'
import { v4 as uuid } from 'uuid'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { db } from '../../../firebase/config'
import { client } from '../../../hooks/Client'
import notDeleted from '../../../helpers/notDeleted'
import useAdressMaterials from '../../../hooks/useAdressMaterials'
import { useFirestoreGeneral } from '../../../firebase/useFirestore'
import runMaterialStockChange from '../../../helpers/materialsHelpers/runMaterialStockChange'
import materialStockDelta from '../../../helpers/materialsHelpers/materialStockDelta'
import materialQuantities from '../../../helpers/materialsHelpers/materialQuantities'
import { newEntryQuantities, plannedPatch, usedPatch, statusPatch } from '../../../helpers/materialsHelpers/materialEntryPatch'
import collectNegativeStockWarnings from '../../../helpers/materialsHelpers/collectNegativeStockWarnings'
import stockColor from '../../../helpers/materialsHelpers/stockColor'
import { isAdvice } from '../../../helpers/materialsHelpers/materialCatalog'
import materialDisplayName, { materialArticleNumber, materialSearchText } from '../../../helpers/materialsHelpers/materialDisplayName'
import { registrationTimestampFor, isFutureDay } from '../../../helpers/materialsHelpers/registrationDate'
import formatEuro from '../../../helpers/formatEuro'
import StockWarningModal from '../../common/StockWarningModal'
import NegativeStockConfirmModal from '../../common/NegativeStockConfirmModal'
import Modal from '../../common/Modal'
import Table from '../../common/table/Table.jsx'
import TableCell from '../../common/table/TableCell.jsx'
import { SavedIcon } from '../../../StateManagment/SavedIcon'
import { Auth } from '../../../StateManagment/Auth.js'
import useSettings from '../../../hooks/Settings'

const COLUMNS = [
    { key: 'material', label: 'MATERIAAL OF ADVIES' },
    { key: 'planned', label: 'MEE TE NEMEN', searchable: false },
    { key: 'used', label: 'GEBRUIKT', searchable: false },
    { key: 'registered', label: 'GEREGISTREERD OP', searchable: false },
    // Alleen op het huishouden: bij welk bezoek de regel hoort (testronde 2026-10-13, R2-B5).
    { key: 'appointment', label: 'AFSPRAAK', searchable: false },
    { key: 'status', label: 'STATUS', searchable: false },
    { key: 'description', label: 'OMSCHRIJVING', searchable: false },
    { key: 'price', label: 'PRIJS/STUK', searchable: false },
    { key: 'subtotal', label: 'SUBTOTAAL', searchable: false },
    { key: 'delete', label: '', searchable: false },
]

// AI-CONTEXT: See docs/features/household-materials.md

// Een coachadvies heeft geen voorraad om op te raken; alleen materialen
// krijgen hier een regel over.
const stockSuffix = (m) => {
    if (isAdvice(m)) return ''
    const qty = Number(m.QuantityInStock) || 0
    const threshold = Number(m.LowStockThreshold) || 0
    const color = stockColor(qty, threshold)
    const unit = m.Unit ? ` ${m.Unit}` : ''

    if (qty < 0) return `${qty}${unit} (tekort)`
    if (color === 'red') return qty <= 0 ? 'geen voorraad' : `${qty}${unit} (geen voorraad)`
    if (color === 'yellow') return `${qty}${unit} (lage voorraad)`
    return `${qty}${unit}`
}

const timestamp = () => firebase.firestore.FieldValue.serverTimestamp()

/** yyyy-mm-dd voor een `<input type='date'>`, lokaal — niet via toISOString. */
const dayValue = (date) => {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

/** Leeg bij een ontbrekende datum — nooit vandaag invullen als er niets staat. */
const dateInputValue = (registrationDate) =>
    (registrationDate && registrationDate.ms !== null ? dayValue(new Date(registrationDate.ms)) : '')

/**
 * Editable materials table for a single household (address). Shared by the
 * Materialen tab and the TaskEditor "Materialen" section — interactive in both
 * view and edit mode.
 *
 * Per regel 'Mee te nemen' en 'Gebruikt' (M4). De voorraad volgt het
 * voorraadeffect (materialStockDelta): mee te nemen gaat eraf bij toevoegen,
 * en zodra 'Gebruikt' is ingevuld telt dat. STATUS is gekoppeld aan
 * 'Gebruikt' (materialEntryPatch); een oude regel die niemand aanraakt blijft
 * zoals hij was.
 * AI-CONTEXT: See docs/features/household-materials.md (Uitbreiding 2026-10, M4)
 *
 * @param {object} props
 * @param {string} props.adressID - Het huishouden (Adresses.ID)
 * @param {boolean} [props.showPrices=true] - Prijzen en totalen tonen (uit in de afspraak)
 * @param {string} [props.taskID] - In een afspraak: de regels van die afspraak en die zonder afspraak; nieuwe regels hangen eraan
 */
const HouseholdMaterialsSection = ({ adressID, showPrices = true, taskID = '' }) => {
    const [, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)

    const { rows, entries, totals, loading, materials, allMaterials } = useAdressMaterials(adressID)
    /** De ruwe regel achter een weergaveregel, voor het voorraadeffect. */
    const entryByID = useMemo(() => new Map(entries.map((e) => [e.ID, e])), [entries])
    /**
     * De aantallen van een nieuwe regel, voor de voorraadcheck én het wegschrijven.
     * @param {number} qty - Mee te nemen
     * @returns {object}
     */
    const newEntry = (qty) => ({ ...newEntryQuantities(qty, taskID), Deducted: true })

    // In een afspraak: zijn eigen regels plus alles wat aan géén afspraak hangt
    // (o.a. alle regels van vóór 2026-10), zodat de bestaande werkwijze in een
    // afspraak blijft werken; regels van een andere afspraak niet (besluit Gijs
    // 2026-10-13). Op het huishouden alles.
    const visibleRows = useMemo(
        () => (taskID
            ? rows.filter((r) => {
                const eigenTaak = entryByID.get(r.ID)?.TaskID
                return !eigenTaak || eigenTaak === taskID
            })
            : rows),
        [rows, entryByID, taskID]
    )

    const [pickerOpen, setPickerOpen] = useState(false)
    const [selected, setSelected] = useState({}) // { [materialID]: quantity }
    const [warningOpen, setWarningOpen] = useState(false)
    const [warningInfo, setWarningInfo] = useState(null)
    const [resetKey, setResetKey] = useState(0)
    // Foutcode van een regel die niet opgeslagen kon worden; leeg = geen melding.
    const [fout, setFout] = useState('')
    // Een schrijfactie loopt: toon 'Opslaan…' en negeer een tweede klik
    // (testronde 2026-10-13, R2-B3). Ref én state: de ref houdt dubbel klikken
    // tegen vóór React opnieuw rendert, de state toont het.
    const bezigRef = useRef(false)
    const [bezig, setBezig] = useState(false)

    // De afspraken van het huishouden, alleen op het tabblad van het huishouden
    // (in een afspraak is de kolom overbodig). '' slaat de query over.
    const tasks = useFirestoreGeneral('Tasks', 'AdressID', taskID ? '' : (adressID || ''))
    const taskByID = useMemo(
        () => new Map((Array.isArray(tasks) ? tasks : []).map((t) => [t.ID, t])),
        [tasks]
    )
    /**
     * @param {object | undefined} entry
     * @returns {string} Datum van de afspraak, '—' zonder afspraak, '' zolang de afspraken laden
     */
    const afspraakLabel = (entry) => {
        if (!entry?.TaskID) return '—'
        if (!Array.isArray(tasks)) return ''
        const taak = taskByID.get(entry.TaskID)
        const datum = taak?.Timestamp?.toDate?.()
        if (!taak) return 'afspraak verwijderd'
        return datum ? dayValue(datum).split('-').reverse().join('-') : 'afspraak zonder datum'
    }

    // Bevestiging vóór een registratie die de voorraad onder 0 brengt. De
    // uit te voeren mutatie wacht in `pendingChange` tot de gebruiker
    // doorklikt; annuleren laat 'm vallen zonder iets weg te schrijven.
    const { allowNegativeStock } = useSettings()
    const [negativeWarnings, setNegativeWarnings] = useState([])
    const [negativeConfirmOpen, setNegativeConfirmOpen] = useState(false)
    const [pendingChange, setPendingChange] = useState(null)

    // Alleen bij mount bepaald: een sessie die over middernacht heen loopt mag
    // geen datumvelden onder de gebruiker vandaan herschrijven.
    const todayValue = useMemo(() => dayValue(new Date()), [])

    // Uit `allMaterials`, met de verwijderde erbij: een regel van een verwijderd
    // materiaal is geen wees, alleen voert de organisatie het niet meer (inbox #284).
    const materialByID = useMemo(() => {
        const map = new Map()
        allMaterials.forEach((m) => map.set(m.ID, m))
        return map
    }, [allMaterials])

    // The TaskEditor hides all pricing; the Materialen tab keeps it.
    const columns = COLUMNS
        .filter((c) => showPrices || (c.key !== 'price' && c.key !== 'subtotal'))
        .filter((c) => !taskID || c.key !== 'appointment')

    const showWarning = (blockedBy) => {
        setWarningInfo(blockedBy)
        setWarningOpen(true)
    }

    /**
     * Voert `run` uit, maar vraagt eerst om bevestiging wanneer een van de
     * mutaties de voorraad onder 0 brengt én dat is toegestaan. Staat negatieve
     * voorraad uit, dan gaat het gewoon door en weigert
     * `runMaterialStockChange` het tekort zelf.
     *
     * @param {Array<{MaterialID: string, delta: number}>} changes
     * @param {() => Promise<void>} run
     * @param {() => void} [onCancel] - Draai de UI terug als de gebruiker afziet
     */
    const withNegativeStockConfirm = async (changes, run, onCancel) => {
        if (!allowNegativeStock) return run()

        // Bewust de LEVENDE lijst: van een verwijderd materiaal houdt Deccos geen
        // voorraad meer bij (runMaterialStockChange slaat het over), dus er valt
        // ook niets te waarschuwen (inbox #284).
        const warnings = collectNegativeStockWarnings(materials, changes)
        if (warnings.length === 0) return run()

        setNegativeWarnings(warnings)
        // Een functie in state moet via de updater-vorm, anders roept React 'm aan.
        setPendingChange(() => ({ run, onCancel }))
        setNegativeConfirmOpen(true)
    }

    const confirmNegativeChange = async () => {
        const pending = pendingChange
        setNegativeConfirmOpen(false)
        setPendingChange(null)
        if (pending) await pending.run()
    }

    // Sluiten zonder bevestigen: niets wegschrijven en de invoervelden
    // terugzetten op wat er is opgeslagen.
    const closeNegativeConfirm = (open) => {
        setNegativeConfirmOpen(open)
        if (!open) {
            if (pendingChange?.onCancel) pendingChange.onCancel()
            setPendingChange(null)
        }
    }

    const openPicker = () => {
        setSelected({})
        setPickerOpen(true)
    }

    const toggleSelected = (materialID) => {
        setSelected((prev) => {
            if (prev[materialID] != null) {
                const next = { ...prev }
                delete next[materialID]
                return next
            }
            return { ...prev, [materialID]: 1 }
        })
    }

    const setSelectedQuantity = (materialID) => (e) => {
        // Keep the raw value while typing (so the field can be cleared); coerce on add.
        const raw = e.target.value
        setSelected((prev) => ({ ...prev, [materialID]: raw === '' ? '' : Number(raw) }))
    }

    // Add every selected material with its chosen quantity. Stock is deducted per
    // material. Materials without enough stock are skipped and surfaced via the warning.
    //
    // ELKE TOEVOEGING WORDT EEN EIGEN REGEL, ook wanneer het materiaal al op dit
    // huishouden staat. Dat was eerder een `update` op de bestaande regel, en
    // daar kan maar één registratiedatum op: een aanvulling in januari trok de
    // registratie van vorig jaar mee naar dit boekjaar, inclusief de waarde die
    // vorig jaar al was gerapporteerd.
    // AI-CONTEXT: See docs/plans/ondersteuningsmodule-code-integratie.md (T1)
    const addSelectedMaterials = async () => {
        const entries = Object.entries(selected)
        if (!adressID || entries.length === 0) {
            setPickerOpen(false)
            return
        }

        const changes = entries.map(([materialID, rawQty]) => ({
            MaterialID: materialID,
            delta: materialStockDelta(null, newEntry(Math.max(1, Number(rawQty) || 1))),
        }))

        // Het keuzevenster gaat eerst dicht: de bevestiging is zelf een venster
        // en twee gestapelde vensters lezen niet.
        setPickerOpen(false)
        await withNegativeStockConfirm(changes, () => writeSelectedMaterials(entries))
    }

    /**
     * Boekt de voorraad, schrijft daarna de regel, en zet de boeking terug als
     * het schrijven mislukt. Zonder dat terugzetten blijft de voorraad
     * verschoven terwijl de regel niet bestaat of niet veranderd is.
     * AI-CONTEXT: See docs/pitfalls.md#state-multi-step-signup-leaves-half-an-account
     * @param {string} materialID
     * @param {number} delta - Voor runMaterialStockChange
     * @param {() => Promise<void>} write - De Firestore-write van de regel
     * @returns {Promise<{ ok?: boolean, failed?: boolean, blockedBy?: object }>}
     */
    const bookAndWrite = async (materialID, delta, write) => {
        if (bezigRef.current) return { busy: true }
        bezigRef.current = true
        setBezig(true)
        try {
            return await bookAndWriteNu(materialID, delta, write)
        } finally {
            bezigRef.current = false
            setBezig(false)
        }
    }

    /** @see bookAndWrite — zonder de bezig-poort. */
    const bookAndWriteNu = async (materialID, delta, write) => {
        setFout('')
        const res = await runMaterialStockChange(db, client, materialID, delta, {
            allowNegative: allowNegativeStock,
        })
        if (res.blockedBy) return res
        try {
            await write()
            return { ok: true }
        } catch (err) {
            setFout(err?.code || err?.message || String(err))
            // Terugzetten mag niet weigeren: het herstelt alleen wat net is geboekt.
            await runMaterialStockChange(db, client, materialID, -delta, { allowNegative: true })
            return { failed: true }
        }
    }

    const writeSelectedMaterials = async (entries) => {
        let blockedBy = null
        let newPosition = rows.length

        for (const [materialID, rawQty] of entries) {
            const qty = Math.max(1, Number(rawQty) || 1)
            const match = materialByID.get(materialID)
            if (!match) continue

            const nieuw = newEntry(qty)
            const position = newPosition + 1
            const res = await bookAndWrite(match.ID, materialStockDelta(null, nieuw), () => db.collection('AdressMaterials').doc().set({
                ID: uuid(),
                CompagnyID: client,
                Compagny: client,
                AdressID: adressID,
                MaterialID: match.ID,
                ...nieuw,
                Description: '',
                Position: position,
                Deleted: false,
                DeletedAt: '',
                DeletedBy: '',
                // Ligt vast op het moment van registreren en beweegt niet mee
                // met latere bewerkingen, anders dan `Timestamp`.
                RegisteredAt: registrationTimestampFor(new Date()),
                // De prijs van dit moment (T20): een latere prijswijziging in de
                // catalogus verschuift een eerdere verantwoording niet. Zelfde
                // lezing als het overzicht deed: geen prijs = 0.
                // AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T20)
                ItemPriceAtRegistration: Number(match.ItemPrice) || 0,
                Timestamp: timestamp(),
            }))
            if (res.blockedBy) {
                if (!blockedBy) blockedBy = res.blockedBy
                continue
            }
            if (res.ok) newPosition = position
        }

        setPickerOpen(false)
        setSelected({})
        setSaved('flex')
        if (blockedBy) showWarning(blockedBy)
    }

    /**
     * Schrijft een wijziging van de aantallen of de status, en boekt eerst het
     * verschil in voorraadeffect. Een regel die nooit van de voorraad af ging
     * (Deducted: false) raakt de voorraad ook nu niet.
     * @param {object} row - De weergaveregel
     * @param {object} patch - Uit materialEntryPatch
     */
    const changeEntry = async (row, patch) => {
        const entry = entryByID.get(row.ID) || null
        if (!entry) return
        const delta = materialStockDelta(entry, { ...entry, ...patch })
        await withNegativeStockConfirm(
            [{ MaterialID: row.materialID, delta }],
            () => writeEntry(row, patch, delta),
            () => setResetKey((k) => k + 1)
        )
    }

    const writeEntry = async (row, patch, delta) => {
        const res = await bookAndWrite(row.materialID, delta,
            () => db.collection('AdressMaterials').doc(row.docid).update({ ...patch, Timestamp: timestamp() }))
        if (res.blockedBy) showWarning(res.blockedBy)
        if (!res.ok) {
            setResetKey((k) => k + 1) // zet de invoervelden terug op wat er is opgeslagen
            return
        }
        setSaved('flex')
    }

    const plannedHandler = (row) => async (e) => {
        const entry = entryByID.get(row.ID)
        const newQty = Number(e.target.value)
        if (!entry || e.target.value === '' || !Number.isFinite(newQty) || newQty < 0) return
        if (newQty === materialQuantities(entry).planned) return
        await changeEntry(row, plannedPatch(entry, newQty))
    }

    const usedHandler = (row) => async (e) => {
        const entry = entryByID.get(row.ID)
        // Leeg = nog niet ingevuld; 0 is wel ingevuld.
        const raw = e.target.value
        const used = raw === '' ? null : Number(raw)
        if (!entry || (used !== null && (!Number.isFinite(used) || used < 0))) return
        if (used === materialQuantities(entry).used) return
        await changeEntry(row, usedPatch(entry, used))
    }

    const statusHandler = (row) => async (e) => {
        const entry = entryByID.get(row.ID)
        if (!entry) return
        await changeEntry(row, statusPatch(entry, e.target.value))
    }

    const descriptionHandler = (row) => async (e) => {
        await db.collection('AdressMaterials').doc(row.docid).update({
            Description: e.target.value,
            Timestamp: timestamp(),
        })
        setSaved('flex')
    }

    // De registratiedatum bepaalt in welk boekjaar deze regel meetelt voor het
    // kengetal Impact per euro. Terug in de tijd mag — later registreren dan het
    // gebeurde is de normale gang van zaken; de toekomst in niet.
    const registrationDateHandler = (row) => async (e) => {
        const gekozen = e.target.value
        const stamp = registrationTimestampFor(gekozen)

        if (!stamp || isFutureDay(gekozen)) {
            setResetKey((k) => k + 1) // zet het veld terug op de opgeslagen datum
            return
        }

        await db.collection('AdressMaterials').doc(row.docid).update({
            RegisteredAt: stamp,
            Timestamp: timestamp(),
        })
        setSaved('flex')
    }

    const deleteHandler = (row) => async () => {
        const entry = entryByID.get(row.ID)
        if (!entry) return
        // Geef het voorraadeffect van de regel terug — alleen wat er echt af ging —
        // en verwijder hem daarna zacht (ADR-008).
        const res = await bookAndWrite(row.materialID, materialStockDelta(entry, null),
            () => db.collection('AdressMaterials').doc(row.docid).update({
                Deleted: true,
                DeletedAt: timestamp(),
                DeletedBy: auth?.ID || '',
            }))
        if (res.ok) setSaved('flex')
    }

    return (
        <div className='household-materials'>
            {loading && <p className='adress-materials-empty'>Laden…</p>}
            {taskID && (
                <p className='household-materials-hint'>Materialen worden direct opgeslagen; 'Annuleren' draait ze niet terug.</p>
            )}
            {bezig && <p className='household-materials-hint'>Opslaan…</p>}
            {fout && (
                <p className='household-materials-error' role='alert'>
                    {`Opslaan is niet gelukt (${fout}). De voorraad is teruggezet; probeer het opnieuw.`}
                </p>
            )}

            {!loading && (
                <Table
                    columns={columns}
                    items={visibleRows}
                    getItemKey={(row) => row.ID}
                    showSearch={visibleRows.length > 6}
                    searchAccessor={(row) => materialSearchText(materialByID.get(row.materialID)) || row.title || ''}
                    searchPlaceholder='Zoek materiaal of advies'
                    emptyMessage={taskID ? 'Nog geen materialen bij deze afspraak.' : 'Nog geen materialen voor dit adres.'}
                    addRow={{ toolTipContent: 'Materiaal of advies toevoegen', onClick: openPicker }}
                    renderRow={(row) => {
                        const m = materialByID.get(row.materialID)
                        const removed = !!m && !notDeleted(m)
                        const aantallen = materialQuantities(entryByID.get(row.ID))
                        const color = m && !removed && !isAdvice(m)
                            ? stockColor(Number(m.QuantityInStock) || 0, Number(m.LowStockThreshold) || 0)
                            : null
                        return (
                            <>
                                <TableCell>
                                    <span className={`household-materials-title ${m ? '' : 'subtask-materials-title-orphan'}`}>
                                        {color && <span className={`materials-stock-dot materials-stock-dot-${color}`} />}
                                        {row.title}
                                        {/* De voorraad ook in tekst: het bolletje alleen zei niets,
                                            en een uitleg bij hover bestaat op de telefoon niet
                                            (testronde 2026-10-13, R2-B4). */}
                                        {m && !removed && !isAdvice(m) && (
                                            <span className='household-materials-stock'>{`voorraad ${stockSuffix(m)}`}</span>
                                        )}
                                        {m && isAdvice(m) && (
                                            <span className='materials-kind-tag'>Coachadvies</span>
                                        )}
                                        {removed && (
                                            <span className='materials-kind-tag'>Verwijderd</span>
                                        )}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    <input
                                        key={`planned-${row.ID}-${resetKey}-${aantallen.planned}`}
                                        className='household-materials-qty-input'
                                        type='number'
                                        min='0'
                                        aria-label='Mee te nemen'
                                        defaultValue={aantallen.planned}
                                        onBlur={plannedHandler(row)}
                                    />
                                    {row.unit && <span className='subtask-materials-unit'>{row.unit}</span>}
                                </TableCell>
                                <TableCell>
                                    <input
                                        key={`used-${row.ID}-${resetKey}-${aantallen.used}`}
                                        className='household-materials-qty-input'
                                        type='number'
                                        min='0'
                                        aria-label='Gebruikt'
                                        defaultValue={aantallen.used === null ? '' : aantallen.used}
                                        onBlur={usedHandler(row)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <input
                                        key={`reg-${row.ID}-${resetKey}`}
                                        className='household-materials-date-input'
                                        type='date'
                                        max={todayValue}
                                        defaultValue={dateInputValue(row.registrationDate)}
                                        onBlur={registrationDateHandler(row)}
                                    />
                                    {row.registrationDate.source === 'legacy' && (
                                        <span
                                            className='household-materials-date-approx'
                                            title='Deze regel is geregistreerd voordat de datum werd vastgelegd. Getoond is de laatste wijziging — pas hem aan als de werkelijke datum anders was.'
                                        >
                                            bij benadering
                                        </span>
                                    )}
                                </TableCell>
                                {!taskID && (
                                    <TableCell>{afspraakLabel(entryByID.get(row.ID))}</TableCell>
                                )}
                                <TableCell>
                                    <select
                                        className='household-materials-status-select'
                                        aria-label='Status'
                                        value={row.status === 'used' ? 'done' : 'toDo'}
                                        onChange={statusHandler(row)}
                                    >
                                        <option value='toDo'>Gepland</option>
                                        <option value='done'>Gebruikt</option>
                                    </select>
                                </TableCell>
                                <TableCell>
                                    <input
                                        className='household-materials-description-input'
                                        type='text'
                                        defaultValue={row.description}
                                        placeholder='Bijv. achterdeur'
                                        onBlur={descriptionHandler(row)}
                                    />
                                </TableCell>
                                {showPrices && <TableCell>{formatEuro(row.unitPrice)}</TableCell>}
                                {showPrices && <TableCell>{formatEuro(row.subtotal)}</TableCell>}
                                <TableCell>
                                    <DeleteOutlinedIcon
                                        className='table-delete-icon'
                                        onClick={deleteHandler(row)}
                                    />
                                </TableCell>
                            </>
                        )
                    }}
                />
            )}

            {showPrices && !loading && visibleRows.length > 0 && (
                <div className='adress-materials-totals'>
                    <div className='adress-materials-totals-row'>
                        <span>Totaal gebruikt</span>
                        <span>{formatEuro(totals.used)}</span>
                    </div>
                    <div className='adress-materials-totals-row'>
                        <span>Totaal gepland</span>
                        <span>{formatEuro(totals.planned)}</span>
                    </div>
                    <div className='adress-materials-totals-row adress-materials-totals-grand'>
                        <span>Totaal</span>
                        <span>{formatEuro(totals.combined)}</span>
                    </div>
                </div>
            )}

            <Modal
                modalOpen={pickerOpen}
                setModalOpen={setPickerOpen}
                confirm={addSelectedMaterials}
                confirmLabel={Object.keys(selected).length > 0 ? `Toevoegen (${Object.keys(selected).length})` : 'Toevoegen'}
                pageHeader='Materialen en adviezen toevoegen'
                content={
                    <div className='household-materials-picker-modal'>
                        {materials.length === 0 ? (
                            <p className='adress-materials-empty'>Geen materialen of adviezen beschikbaar.</p>
                        ) : (
                            <ul className='household-materials-picker-list'>
                                {materials.map((m) => {
                                    const advice = isAdvice(m)
                                    const color = stockColor(
                                        Number(m.QuantityInStock) || 0,
                                        Number(m.LowStockThreshold) || 0
                                    )
                                    const isSelected = selected[m.ID] != null
                                    return (
                                        <li key={m.ID} className='household-materials-picker-item'>
                                            <label className='household-materials-picker-label'>
                                                <input
                                                    type='checkbox'
                                                    checked={isSelected}
                                                    onChange={() => toggleSelected(m.ID)}
                                                />
                                                {!advice && (
                                                    <span className={`materials-stock-dot materials-stock-dot-${color}`} />
                                                )}
                                                <span className='household-materials-picker-title'>
                                                    {materialDisplayName(m)}{m.Category ? ` — ${m.Category}` : ''}
                                                </span>
                                                {materialArticleNumber(m) && (
                                                    <span className='materials-article-number'>
                                                        {materialArticleNumber(m)}
                                                    </span>
                                                )}
                                                {advice && (
                                                    <span className='materials-kind-tag'>Coachadvies</span>
                                                )}
                                            </label>
                                            {isSelected && (
                                                <input
                                                    className='household-materials-picker-qty'
                                                    type='number'
                                                    min='1'
                                                    value={selected[m.ID]}
                                                    onChange={setSelectedQuantity(m.ID)}
                                                    aria-label={`Aantal ${materialDisplayName(m)}`}
                                                />
                                            )}
                                            <span className='household-materials-picker-stock'>{stockSuffix(m)}</span>
                                        </li>
                                    )
                                })}
                            </ul>
                        )}
                    </div>
                }
            />

            <StockWarningModal
                modalOpen={warningOpen}
                setModalOpen={setWarningOpen}
                blockedBy={warningInfo}
            />

            <NegativeStockConfirmModal
                modalOpen={negativeConfirmOpen}
                setModalOpen={closeNegativeConfirm}
                warnings={negativeWarnings}
                confirm={confirmNegativeChange}
            />
        </div>
    )
}

export default HouseholdMaterialsSection
